import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, RedirectCommand, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { map, skipWhile, switchMap } from 'rxjs/operators';
import { isCanvasRendererHost } from 'src/app/canvas-renderer/canvas-renderer-host';
import { logger } from 'src/app/pbir/utils/logger';
import { ContentAccessApiService, UserAccessContext, UserContentType } from 'src/app/services/api.services';
import { environment } from 'src/environments/environment';
import { catchAppError, isNullOrUndefined } from '../utils';
import { of } from 'rxjs';
import { SnackService } from 'src/app/services/snack.service';

const LOGGER = logger('ContentAccessGuard');

@Injectable({
  providedIn: 'root'
})
export class ContentAccessGuard implements CanActivate, CanActivateChild {
  constructor(
    private readonly contentAccessApi: ContentAccessApiService,
    private readonly router: Router,
    private readonly snack: SnackService) { }

  canActivate(route: ActivatedRouteSnapshot) {
    return this.handle(route);
  }

  canActivateChild(route: ActivatedRouteSnapshot) {
    return this.handle(route);
  }

  private handle(route: ActivatedRouteSnapshot) {
    if (environment.testing || isCanvasRendererHost) {
      return true;
    }

    let data =
      (route.data instanceof ContentAccessGuardData) 
        ? route.data
        : (route.data.access instanceof ContentAccessGuardData)
          ? route.data.access
          : null;

    if (!(data instanceof ContentAccessGuardData)) {
      LOGGER.error(`Missing ContentAccessGuardData on the '${route.toString()}' route`);
      return true;
    }
    
    let contentIdParam = route.paramMap.get(data.config.idParam ?? 'id') as any;
    let contentId;

    if (isNullOrUndefined(contentIdParam)) {
      LOGGER.error(
        `Param '${data.config.idParam}' is null/undefined when executing the '${route.toString()}' route`);

      return true;
    }

    if (typeof contentIdParam === 'number') {
      contentId = contentIdParam;
    }
    else {
      contentId = parseInt('' + contentIdParam);
    }
    
    if (isNaN(contentId) || contentId < 1) {
      LOGGER.error(
        `Param '${data.config.idParam}' has invalid value '${contentIdParam}' executing the '${route.toString()}' route`);

      return true;
    }

    let failureResult = 
      data.config.redirectTo 
        ? of(new RedirectCommand(this.router.parseUrl(data.config.redirectTo)))
        : of(false);

    return this.contentAccessApi.getAccess(
      data.type,
      contentId,
      data.context
    ).pipe(
      switchMap(r => {
        let canContinue = 
          (!data.kind.view   || r.view) &&
          (!data.kind.edit   || r.edit) &&
          (!data.kind.delete || r.delete) &&
          (!data.kind.copy   || r.copy);

        if (!canContinue) {
          this.snack.openError(`This link is invalid`);
          return failureResult;
        }

        return of(true);
      }),
      catchAppError(err => {
        LOGGER.error(`Unexpected error when checking content access`, err);
        return failureResult;
      }, { showErrorSnack: true, errorMessage: `An unexpected error occurred` })
    );
  }
}

export interface ContentAccessKind {
  readonly view?: boolean;
  readonly edit?: boolean;
  readonly delete?: boolean;
  readonly copy?: boolean;
}

export namespace ContentAccessKind {
  export const view: ContentAccessKind = { view: true };
  export const copy: ContentAccessKind = { copy: true };
  export const edit: ContentAccessKind = { edit: true };
}

export class ContentAccessGuardData {
  constructor(
    public readonly context: UserAccessContext,
    public readonly type: UserContentType,
    public readonly kind: ContentAccessKind,
    public readonly config: {
      readonly idParam?: string; // defaults to id
      readonly redirectTo?: string;
    }) { }
}

export function withContentAccessGuard<T extends Record<string, any>>(
  route: T,
  config: {
    readonly context: UserAccessContext;
    readonly type: UserContentType;
    readonly kind: ContentAccessKind;
    readonly idParam?: string; // defaults to id
    readonly redirectTo?: string;
  }): T {
  return {
    ...route,
    canActivate: [ContentAccessGuard, ...(route.canActivate ?? [])],
    canActivateChild: [ContentAccessGuard, ...(route.canActivateChild ?? [])],
    data: {
      ...route.data,
      access: new ContentAccessGuardData(config.context, config.type, config.kind, {
        idParam: config.idParam,
        redirectTo: config.redirectTo
      })
    }
  };
}

export type ContentAccessGuardFactory<T extends Record<string, any>> = 
  (context: UserAccessContext, kind: ContentAccessKind, route: T) => T;

export function contentAccessGuardFactory(
  type: UserContentType, 
  config?: {
    readonly idParam?: string; // defaults to id
    readonly redirectTo?: string;
  }): ContentAccessGuardFactory<unknown> {
  return (context, kind, route) => {
    return withContentAccessGuard<unknown>(route, {
      context,
      type,
      kind,
      idParam: config?.idParam,
      redirectTo: config?.redirectTo,
    });
  };
}